/**
 * Agent Run Phases — the phase runner's vocabulary, envelopes, gate result,
 * phase events and report.
 *
 * A factory lane is an ordered list of steps (`tooling/coordinator/lanes/`).
 * Each step is a CODE phase, a deterministic command, or an AGENT phase, one
 * bounded `claude -p` session that must return a typed envelope. The runner
 * validates every envelope against the schema registered here, a code gate
 * checks it, and the runner — not the agent — records the step's start and
 * done edges in the ledger. Everything that crosses a process or a network
 * boundary in that loop is declared once, here: the runner, the gates, the
 * backend routes and `sj` all import it.
 *
 * ⚠️ THIS FILE IS A PROTECTED PATH (KTD11). A factory build phase may not edit
 * it: an agent that could widen its own envelope schema could make any claim
 * pass. The build gate and the settings hook both deny it.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-09-20-1756-feat-software-factory-phase-runner-plan.md
 */

import { z } from 'zod'

import { AgentRunLaneSchema, ResearchPacketSchema, RiskFlagsSchema, SizeClassSchema } from './agent-run'

// =============================================================================
// Phase names (KTD22)
// =============================================================================

/**
 * The phases that run a model. Each one has an envelope in
 * {@link PHASE_ENVELOPE_SCHEMAS}, and only these may carry a budget, a model or
 * a correction cap in config (`factory.phases`, `factory.routing`).
 */
export const AGENT_PHASE_NAMES = ['triage', 'build', 'address-findings', 'research', 'retro'] as const

/** The deterministic phases: no model, no envelope, no budget. */
export const CODE_PHASE_NAMES = [
  'checkout',
  'recheck',
  'verify',
  'commit',
  'review',
  'review-final',
  'pr',
  'evidence'
] as const

/**
 * Every phase name, in the order a bug-lane run first meets them, with the
 * investigate lane's own two (`research`, `evidence`) before the `retro` both
 * lanes end on.
 *
 * A NAME, NOT A STEP. A lane may run the same phase more than once — the bug
 * lane runs `verify` and `commit` twice — so events and the phase cursor key on
 * the step's INDEX in the lane, and readers render over the run's lane steps,
 * never over this list.
 */
export const PHASE_NAMES = [
  'checkout',
  'triage',
  'recheck',
  'build',
  'verify',
  'commit',
  'review',
  'address-findings',
  'review-final',
  'pr',
  'research',
  'evidence',
  'retro'
] as const satisfies readonly (AgentPhaseName | CodePhaseName)[]

export const AgentPhaseNameSchema = z.enum(AGENT_PHASE_NAMES)
export type AgentPhaseName = z.infer<typeof AgentPhaseNameSchema>

export const CodePhaseNameSchema = z.enum(CODE_PHASE_NAMES)
export type CodePhaseName = z.infer<typeof CodePhaseNameSchema>

export const PhaseNameSchema = z.enum(PHASE_NAMES)
export type PhaseName = z.infer<typeof PhaseNameSchema>

export function isAgentPhaseName(value: string): value is AgentPhaseName {
  return (AGENT_PHASE_NAMES as readonly string[]).includes(value)
}

export const PHASE_KINDS = ['code', 'agent'] as const

export const PhaseKindSchema = z.enum(PHASE_KINDS)
export type PhaseKind = z.infer<typeof PhaseKindSchema>

/** A phase's kind follows from its name; nothing else may declare it. */
export function phaseKindOf(phase: PhaseName): PhaseKind {
  return isAgentPhaseName(phase) ? 'agent' : 'code'
}

// =============================================================================
// Lane steps
// =============================================================================

/**
 * One step of a lane. Two shapes rather than one with a free `kind`, so a
 * `{ phase: 'commit', kind: 'agent' }` step does not typecheck, let alone parse.
 */
export const LaneStepSchema = z.union([
  z.object({ index: z.number().int().min(0), phase: AgentPhaseNameSchema, kind: z.literal('agent') }),
  z.object({ index: z.number().int().min(0), phase: CodePhaseNameSchema, kind: z.literal('code') })
])
export type LaneStep = z.infer<typeof LaneStepSchema>

/** A whole lane: non-empty, and every step's `index` is its position. */
export const LaneStepsSchema = z
  .array(LaneStepSchema)
  .min(1)
  .refine(steps => steps.every((step, position) => step.index === position), {
    message: "every step's index must equal its position in the lane"
  })

// =============================================================================
// Envelopes — what an agent phase returns
// =============================================================================

/**
 * A repo-relative path as an envelope names it. Relative, forward-slashed, no
 * `..` segment and no leading `-`: a gate hands these to `git`, prettier and
 * vitest, and a path that climbs out of the tree or reads as a flag is not a
 * file claim.
 */
export const REPO_PATH_MAX = 200

export const RepoPathSchema = z
  .string()
  .min(1)
  .max(REPO_PATH_MAX)
  .refine(
    path =>
      !path.startsWith('/') &&
      !path.startsWith('-') &&
      !path.includes('\\') &&
      !path.includes('\0') &&
      !path.split('/').includes('..'),
    { message: 'must be a repo-relative path with no `..` segment and no leading `/` or `-`' }
  )

/** At most this many paths in one envelope list; the phase event size bound is the real ceiling. */
export const ENVELOPE_PATHS_MAX = 30

/** The longest free-text field in an envelope. */
export const ENVELOPE_TEXT_MAX = 2000

const envelopeText = z.string().min(1).max(ENVELOPE_TEXT_MAX)

/**
 * The only shape a repro command may take (KTD25):
 * `pnpm --filter ./<package dir> exec vitest run <path>`, single-spaced, where
 * `<path>` is REPO-relative and sits under the package directory.
 *
 * CLOSED BECAUSE THE GATE EXECUTES IT. Triage runs with mutation denied, but a
 * free-text command the repro gate shell-executed would hand any
 * prompt-injected instruction arbitrary execution on the VM, in the process
 * tree that holds the run's credentials. The gate runs {@link ParsedReproCommand}
 * `argv` with no shell; the token alphabet additionally keeps shell
 * metacharacters out, so the closed form stays safe even when pasted into a
 * shell by hand. (A test under an App Router `(group)` or `[param]` directory
 * cannot be a repro for that reason.)
 *
 * WHY THE PACKAGE IS A DIRECTORY AND THE PATH IS REPO-RELATIVE. The path must
 * fall inside `filesInScope`, which is repo-relative, while `pnpm --filter`
 * runs vitest from the package directory, where a repo-relative filter matches
 * no file — and vitest exits 1 on "no test files found", which a must-fail gate
 * would read as a reproduced bug. So the envelope names the path the way scope
 * does, and `argv` carries it rewritten relative to the package. The gate must
 * still confirm the file exists before it trusts a failing exit.
 */
const REPRO_COMMAND_PATTERN = /^pnpm --filter (\S+) exec vitest run (\S+)$/
const REPRO_PACKAGE_PATTERN = /^\.\/[A-Za-z0-9@._-][A-Za-z0-9@._/-]*[A-Za-z0-9@._-]$|^\.\/[A-Za-z0-9@._-]$/
const REPRO_PATH_PATTERN = /^[A-Za-z0-9@._][A-Za-z0-9@._/-]*$/
const REPRO_TEST_FILE_PATTERN = /\.(test|spec)\.(ts|tsx|mts|js|mjs)$/

export interface ParsedReproCommand {
  /** The package directory, repo-relative, without the `./`. */
  packageDir: string
  /** The test path as the envelope names it, repo-relative. */
  path: string
  /** What the repro gate executes from the repo root, with no shell. */
  argv: readonly string[]
}

/** Parse a repro command, or null when it is not the closed form. */
export function parseReproCommand(command: string): ParsedReproCommand | null {
  const match = REPRO_COMMAND_PATTERN.exec(command)
  if (!match) return null
  const [, pkg, path] = match
  if (!pkg || !path) return null
  if (!REPRO_PACKAGE_PATTERN.test(pkg) || !REPRO_PATH_PATTERN.test(path)) return null
  const packageDir = pkg.slice(2)
  if (packageDir.split('/').includes('..') || !RepoPathSchema.safeParse(path).success) return null
  if (!path.startsWith(`${packageDir}/`)) return null
  const packagePath = path.slice(packageDir.length + 1)
  // No segment may read as a flag: `apps/backend/-u` would reach vitest as `-u`.
  if (path.split('/').some(segment => segment.startsWith('-'))) return null
  // A test file, never a directory or a source file: either one can match no
  // test, and vitest's exit 1 on "no test files found" would read as a repro.
  if (!REPRO_TEST_FILE_PATTERN.test(path)) return null
  return { packageDir, path, argv: ['pnpm', '--filter', pkg, 'exec', 'vitest', 'run', packagePath] }
}

/** True when `path` is a `filesInScope` entry or sits under one used as a directory. */
export function isPathInScope(path: string, filesInScope: readonly string[]): boolean {
  return filesInScope.some(entry => {
    const dir = entry.endsWith('/') ? entry : `${entry}/`
    return path === entry || path.startsWith(dir)
  })
}

/**
 * `triage` — the diagnosis. Everything here is either checked by a gate (the
 * repro must fail, the paths must exist, the size must agree with the
 * classifier's hypothesis) or read by the build phase.
 */
export const TriageEnvelopeSchema = z
  .object({
    rootCause: envelopeText,
    filesInScope: z.array(RepoPathSchema).min(1).max(ENVELOPE_PATHS_MAX),
    reproCommand: z
      .string()
      .min(1)
      .max(REPO_PATH_MAX * 2),
    sizeClass: SizeClassSchema,
    riskFlags: RiskFlagsSchema,
    notesForNextPhase: z.string().max(ENVELOPE_TEXT_MAX)
  })
  .superRefine((envelope, ctx) => {
    const parsed = parseReproCommand(envelope.reproCommand)
    if (!parsed) {
      ctx.addIssue({
        code: 'custom',
        path: ['reproCommand'],
        message: 'must be exactly `pnpm --filter ./<package dir> exec vitest run <path under that dir>`'
      })
      return
    }
    if (!isPathInScope(parsed.path, envelope.filesInScope)) {
      ctx.addIssue({ code: 'custom', path: ['reproCommand'], message: 'the repro path must fall inside filesInScope' })
    }
  })
export type TriageEnvelope = z.infer<typeof TriageEnvelopeSchema>

/** `build` — what changed. The scope gate compares `filesChanged` with the tree's own change set. */
export const BuildEnvelopeSchema = z.object({
  filesChanged: z.array(RepoPathSchema).min(1).max(ENVELOPE_PATHS_MAX),
  testFilesAdded: z.array(RepoPathSchema).max(ENVELOPE_PATHS_MAX),
  summary: envelopeText
})
export type BuildEnvelope = z.infer<typeof BuildEnvelopeSchema>

/** At most this many rejected review findings in one `address-findings` envelope. */
export const REJECTED_FINDINGS_MAX = 10

/** The longest quoted finding, and the longest reason for rejecting it. */
export const REJECTED_FINDING_MAX = 300
export const REJECTED_FINDING_REASON_MAX = 500

/**
 * `address-findings` — a second build pass over review findings, checked by
 * the same scope gate as `build`, plus the findings the agent declined.
 *
 * A PASS THAT CHANGES NOTHING IS LEGITIMATE ONLY WITH A REASON PER FINDING.
 * Review findings are candidates, not verdicts, so an agent that judges every
 * one wrong must be able to finish; with `filesChanged` required non-empty it
 * could not, and the run parked on a correct refusal. An empty `filesChanged`
 * is therefore accepted when `rejectedFindings` names each declined finding
 * and why, and the lane skips the re-verify and re-commit over it. The reasons
 * land in the PR body, where the human reviewer judges them.
 */
export const AddressFindingsEnvelopeSchema = z
  .object({
    filesChanged: z.array(RepoPathSchema).max(ENVELOPE_PATHS_MAX),
    testFilesAdded: z.array(RepoPathSchema).max(ENVELOPE_PATHS_MAX),
    summary: envelopeText,
    rejectedFindings: z
      .array(
        z.object({
          finding: z.string().min(1).max(REJECTED_FINDING_MAX),
          reason: z.string().min(1).max(REJECTED_FINDING_REASON_MAX)
        })
      )
      .max(REJECTED_FINDINGS_MAX)
      .optional()
  })
  .refine(envelope => envelope.filesChanged.length > 0 || (envelope.rejectedFindings?.length ?? 0) > 0, {
    message: 'change at least one file, or list every finding you reject with a reason in rejectedFindings',
    path: ['filesChanged']
  })
export type AddressFindingsEnvelope = z.infer<typeof AddressFindingsEnvelopeSchema>

/**
 * `research` — the investigate lane's evidence packet, which is also what the
 * run stores as `outcomes.research`. One schema for both, so the envelope the
 * evidence gate accepted is exactly the packet the row carries.
 */
const ResearchEnvelopeSchema = ResearchPacketSchema

/**
 * `retro` — the six retro headings as keys (`tooling/coordinator/README.md`
 * § Retro), each non-empty (an empty section says `none`), plus the PR the retro
 * claims, which the retro gate compares with the run's own.
 */
export const RetroEnvelopeSchema = z.object({
  briefGaps: envelopeText,
  toolingFailures: envelopeText,
  unexpectedStops: envelopeText,
  timeSinks: envelopeText,
  classifier: envelopeText,
  mode: envelopeText,
  prUrl: z
    .string()
    .regex(/^https:\/\/github\.com\/[^/\s]+\/[^/\s]+\/pull\/\d+$/, 'must be a GitHub pull request URL')
    .nullable()
})
export type RetroEnvelope = z.infer<typeof RetroEnvelopeSchema>

/**
 * Agent phase → envelope schema.
 *
 * `satisfies Record<AgentPhaseName, …>`: adding a name to
 * {@link AGENT_PHASE_NAMES} without an envelope is a compile error here, and a
 * code phase name is not a key. `address-findings` is the build envelope plus
 * `rejectedFindings` — a second build pass over review findings, checked by the
 * same gates.
 */
export const PHASE_ENVELOPE_SCHEMAS = {
  triage: TriageEnvelopeSchema,
  build: BuildEnvelopeSchema,
  'address-findings': AddressFindingsEnvelopeSchema,
  research: ResearchEnvelopeSchema,
  retro: RetroEnvelopeSchema
} as const satisfies Record<AgentPhaseName, z.ZodType>

export type PhaseEnvelope<P extends AgentPhaseName> = z.infer<(typeof PHASE_ENVELOPE_SCHEMAS)[P]>

/**
 * An envelope a gate accepted, tagged with the step that produced it. The
 * brief replays these on resume, in step order, and the report file carries
 * them; the union parses each body against its own phase's schema.
 */
const acceptedStepIndex = z.number().int().min(0)

export const AcceptedEnvelopeSchema = z.discriminatedUnion('phase', [
  z.object({ phase: z.literal('triage'), stepIndex: acceptedStepIndex, envelope: TriageEnvelopeSchema }),
  z.object({ phase: z.literal('build'), stepIndex: acceptedStepIndex, envelope: BuildEnvelopeSchema }),
  z.object({
    phase: z.literal('address-findings'),
    stepIndex: acceptedStepIndex,
    envelope: AddressFindingsEnvelopeSchema
  }),
  z.object({ phase: z.literal('research'), stepIndex: acceptedStepIndex, envelope: ResearchEnvelopeSchema }),
  z.object({ phase: z.literal('retro'), stepIndex: acceptedStepIndex, envelope: RetroEnvelopeSchema })
])
export type AcceptedEnvelope = z.infer<typeof AcceptedEnvelopeSchema>

// =============================================================================
// Gate result (KTD21)
// =============================================================================

/**
 * The one gate vocabulary for the phase runner: every gate in
 * `tooling/coordinator/gates/` returns it, and so does the backend's
 * `/gate/recheck`. The caller derives continue-or-park from `verdict`.
 *
 * `violations` is non-empty exactly when the verdict is `fail`: a failure with
 * no reason cannot be answered, and a pass carrying violations is a verdict
 * that disagrees with itself. Informational remarks on a pass (the re-check's
 * "raised to PLAN") go in `notes`.
 */
export const GATE_VERDICTS = ['pass', 'fail', 'skipped'] as const

export const GateVerdictSchema = z.enum(GATE_VERDICTS)
export type GateVerdict = z.infer<typeof GateVerdictSchema>

/** One violation message, truncated to this many characters; the full report lives in the park report file. */
export const GATE_VIOLATION_MESSAGE_MAX = 500

export const GateViolationSchema = z.object({
  /** Kebab-case, e.g. `repro-passed`, `protected-path`, `config-lane-version`. */
  code: z
    .string()
    .max(64)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'must be a kebab-case code'),
  /** Non-empty: a violation nobody can act on is not a reason to park. */
  message: z.string().min(1).max(GATE_VIOLATION_MESSAGE_MAX),
  /** The commit a change-set gate measured against, so a disagreement is diagnosable. */
  baseline: z.string().max(64).optional()
})
export type GateViolation = z.infer<typeof GateViolationSchema>

/**
 * The prefix of every violation code that blames the ENVIRONMENT, not the agent
 * (factory operator plan, KTD12): an unbuilt workspace `dist`, a baseline that
 * could not be taken or is gone. The runner parks on one instead of handing it to
 * an agent correction, so it never spends a correction or touches `correction-cap`;
 * the learning read attributes it to the environment. Codes in use:
 * `env-dist-missing`, `env-workspace-unreadable`, `env-baseline-failed`,
 * `env-baseline-missing`.
 */
const ENVIRONMENT_VIOLATION_PREFIX = 'env-'

export function isEnvironmentViolation(violation: Pick<GateViolation, 'code'>): boolean {
  return violation.code.startsWith(ENVIRONMENT_VIOLATION_PREFIX)
}

/** Cut a message to the stored bound, marking the cut. */
export function truncateViolationMessage(message: string): string {
  if (message.length <= GATE_VIOLATION_MESSAGE_MAX) return message
  return `${message.slice(0, GATE_VIOLATION_MESSAGE_MAX - 1)}…`
}

/**
 * One thing a gate verified, passing or not: "repro exits non-zero", "12 files in
 * change set", "no protected path touched". A verdict says whether to continue;
 * checks say what the verdict rests on, so a green gate that checked nothing is
 * visible as one — and so a review of false triggers can see what a gate looked at.
 *
 * Bounded so a done event still fits {@link PHASE_EVENT_PAYLOAD_MAX_CHARS}; the
 * runner trims checks before violations when an event is over the bound.
 */
export const GATE_CHECKS_MAX = 30
export const GATE_CHECK_ITEM_MAX = 200
export const GATE_CHECK_NOTE_MAX = 300

export const GateCheckSchema = z.object({
  item: z.string().min(1).max(GATE_CHECK_ITEM_MAX),
  ok: z.boolean(),
  note: z.string().max(GATE_CHECK_NOTE_MAX).optional()
})
export type GateCheck = z.infer<typeof GateCheckSchema>

/** Cut a check's text to the stored bounds, marking each cut. */
export function boundGateCheck(check: GateCheck): GateCheck {
  const cut = (text: string, max: number) => (text.length <= max ? text : `${text.slice(0, max - 1)}…`)
  return {
    item: cut(check.item, GATE_CHECK_ITEM_MAX),
    ok: check.ok,
    ...(check.note === undefined ? {} : { note: cut(check.note, GATE_CHECK_NOTE_MAX) })
  }
}

export const GateResultSchema = z
  .object({
    verdict: GateVerdictSchema,
    violations: z.array(GateViolationSchema).max(20),
    notes: z.array(z.string().max(GATE_VIOLATION_MESSAGE_MAX)).max(10).optional(),
    /** Optional and additive: results recorded before checks existed still parse. */
    checks: z.array(GateCheckSchema).max(GATE_CHECKS_MAX).optional()
  })
  .refine(result => (result.verdict === 'fail') === result.violations.length > 0, {
    message: 'violations must be non-empty exactly when the verdict is fail'
  })
export type GateResult = z.infer<typeof GateResultSchema>

// =============================================================================
// Phase events (KTD6)
// =============================================================================

/**
 * The largest serialized `run.phase` payload, in characters.
 *
 * Asserted by the schema so the route can answer 400 BEFORE the recorder runs:
 * past 16,384 characters `boundPayloadSize` stores a truncation stub
 * permanently, under a dedupe key a retry can never replace. The gap between
 * the two numbers is headroom for the `attempt` field the recorder adds
 * server-side.
 */
export const PHASE_EVENT_PAYLOAD_MAX_CHARS = 12_288

/**
 * Minted once per `phase-run.ts` process. Colon-free, because it is the first
 * segment of the colon-separated dedupe key.
 */
export const RunnerInvocationIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{1,64}$/, 'must be 1-64 characters of [A-Za-z0-9_-]')

export const PHASE_EVENT_EDGES = ['start', 'done'] as const

export const PhaseEventEdgeSchema = z.enum(PHASE_EVENT_EDGES)
export type PhaseEventEdge = z.infer<typeof PhaseEventEdgeSchema>

/**
 * The ledger dedupe key for one edge of one step attempt:
 * `${runnerInvocationId}:${stepIndex}:${phase}:${phaseAttempt}:${edge}`.
 *
 * The step INDEX, because a name-keyed id would dedupe the second `verify` into
 * the first; the invocation id, because a resume is a new process that must
 * write new events rather than dedupe into the previous attempt's trail.
 */
export function phaseEventFindingId(key: {
  runnerInvocationId: string
  stepIndex: number
  phase: PhaseName
  phaseAttempt: number
  edge: PhaseEventEdge
}): string {
  return `${key.runnerInvocationId}:${key.stepIndex}:${key.phase}:${key.phaseAttempt}:${key.edge}`
}

const phaseEventIdentity = {
  /** Must equal {@link phaseEventFindingId} over the fields below — the backend dedupes on it. */
  findingId: z.string().min(1).max(200),
  runnerInvocationId: RunnerInvocationIdSchema,
  stepIndex: z.number().int().min(0),
  phase: PhaseNameSchema,
  kind: PhaseKindSchema,
  /** 1-based, per phase session; distinct from the run-level `attempt` (KTD13). */
  phaseAttempt: z.number().int().min(1),
  laneVersion: z.string().min(1).max(64),
  /** The `claude -p` session id the runner minted for an agent phase; null on a code phase. */
  sessionId: z.string().min(1).max(64).nullable()
}

export const PhaseStartEventSchema = z.object({
  edge: z.literal('start'),
  ...phaseEventIdentity
})

export const PhaseDoneEventSchema = z.object({
  edge: z.literal('done'),
  ...phaseEventIdentity,
  verdict: GateVerdictSchema,
  /** Codes plus one truncated message each; the full gate report is in the park report file. */
  violations: z.array(GateViolationSchema).max(20),
  /** What the step's gates verified; trimmed first when the event is over the size bound. */
  checks: z.array(GateCheckSchema).max(GATE_CHECKS_MAX).optional(),
  durationMs: z.number().int().nonnegative(),
  /** This step's own cost — reporting detail; `AgentRun.cost_usd` is the authoritative total (KTD23). */
  costUsd: z.number().nonnegative().nullable(),
  /**
   * Token counts, as `{ input, output }`. NOT `inputTokens`/`outputTokens`:
   * the operations-event redactor blanks any key containing "token", which
   * would store `"[redacted]"` where this schema requires a number.
   */
  usage: z.object({ input: z.number().int().nonnegative(), output: z.number().int().nonnegative() }).nullable(),
  /**
   * The accepted envelope body, present exactly on an agent step that passed.
   * The brief replays these on resume, which is why the body rides the event
   * rather than a digest of it.
   */
  envelope: z.unknown().nullable()
})

function checkPhaseEvent(
  event: z.infer<typeof PhaseStartEventSchema> | z.infer<typeof PhaseDoneEventSchema>,
  ctx: z.RefinementCtx
): void {
  if (event.findingId !== phaseEventFindingId(event)) {
    ctx.addIssue({ code: 'custom', path: ['findingId'], message: 'findingId does not match the event key' })
  }
  if (event.kind !== phaseKindOf(event.phase)) {
    ctx.addIssue({ code: 'custom', path: ['kind'], message: `${event.phase} is a ${phaseKindOf(event.phase)} phase` })
  }
  if ((event.kind === 'code') !== (event.sessionId === null)) {
    ctx.addIssue({
      code: 'custom',
      path: ['sessionId'],
      message: 'an agent phase carries its session id (minted before spawn); a code phase has none'
    })
  }
  if (JSON.stringify(event).length > PHASE_EVENT_PAYLOAD_MAX_CHARS) {
    ctx.addIssue({
      code: 'custom',
      message: `serialized payload exceeds ${PHASE_EVENT_PAYLOAD_MAX_CHARS} characters`
    })
  }
  if (event.edge !== 'done') return

  if ((event.verdict === 'fail') !== event.violations.length > 0) {
    ctx.addIssue({
      code: 'custom',
      path: ['violations'],
      message: 'violations must be non-empty exactly when the verdict is fail'
    })
  }
  const carriesEnvelope = event.envelope !== null && event.envelope !== undefined
  const owesEnvelope = event.verdict === 'pass' && isAgentPhaseName(event.phase)
  if (carriesEnvelope !== owesEnvelope) {
    ctx.addIssue({
      code: 'custom',
      path: ['envelope'],
      message: 'an envelope rides exactly the done event of an agent step that passed'
    })
    return
  }
  if (owesEnvelope && isAgentPhaseName(event.phase)) {
    const parsed = PHASE_ENVELOPE_SCHEMAS[event.phase].safeParse(event.envelope)
    if (!parsed.success) {
      ctx.addIssue({ code: 'custom', path: ['envelope'], message: `not a valid ${event.phase} envelope` })
    }
  }
}

/**
 * The `run.phase` outcome event payload: one step attempt's start or done edge.
 *
 * STRICTER THAN ITS SIBLINGS IN `agent-run-outcome-events.ts`, deliberately.
 * Those describe writes against third-party systems, where a partial answer is
 * a real outcome; this one is written by our own runner, the brief replays
 * envelopes out of it, and a malformed row would be replayed into a resumed
 * run. It is also enforced at the phase-complete route, not only read.
 */
export const PhaseEventPayloadSchema = z
  .discriminatedUnion('edge', [PhaseStartEventSchema, PhaseDoneEventSchema])
  .superRefine(checkPhaseEvent)
export type PhaseEventPayload = z.infer<typeof PhaseEventPayloadSchema>

/** The done edge alone, with the same checks — what the phase-complete route accepts. */
export const PhaseDoneEventPayloadSchema = PhaseDoneEventSchema.superRefine(checkPhaseEvent)
export type PhaseDoneEventPayload = z.infer<typeof PhaseDoneEventPayloadSchema>

/**
 * The start edge alone, with the same checks — the only `phase` payload a run
 * may post through `POST …/events`. A done edge must go through phase-complete,
 * where it moves the cursor in the same transaction.
 */
export const PhaseStartEventPayloadSchema = PhaseStartEventSchema.superRefine(checkPhaseEvent)
export type PhaseStartEventPayload = z.infer<typeof PhaseStartEventPayloadSchema>

// =============================================================================
// Runner exit contract and report (KTD14)
// =============================================================================

/** `phase-run.ts` exit codes; `vm-run.ts` maps them to run-level events. */
export const PHASE_RUNNER_EXIT_CODES = {
  complete: 0,
  failed: 1,
  /** The control plane said stop (heartbeat `stop`, `SIGTERM`). */
  stopped: 3,
  parked: 10,
  /**
   * A file-sink run reached its `phases.stopAfterStep` (KTD11): the slice it was asked for is
   * done. Distinct from `parked` so nobody relays a park question that does not exist, and from
   * `complete` because the lane did not finish. The ledger sink never reports it; there the
   * same stop parks with `configured-stop`, so the run stays resumable.
   */
  'range-end': 11
} as const

export type PhaseRunnerOutcome = keyof typeof PHASE_RUNNER_EXIT_CODES

/**
 * An envelope the runner did not accept — it failed its schema, a gate, or the
 * code step that checks the agent's work (`verify` after `build`) — kept for the
 * false-trigger review: a gate that fires on legitimate work is relaxed under
 * delivery pressure, and nobody can tell which firings were legitimate without
 * the envelope the gate judged. Report file only, never a ledger event.
 */
export const REJECTED_ENVELOPES_MAX = 10
export const REJECTED_ENVELOPE_MAX_CHARS = 4000

export const RejectedEnvelopeSchema = z
  .object({
    /** The step whose check rejected it: the agent step itself, or the code step that corrects it. */
    stepIndex: z.number().int().min(0),
    phase: PhaseNameSchema,
    /** That step's `phaseAttempt`. */
    attempt: z.number().int().min(1),
    /** Scrubbed; past {@link REJECTED_ENVELOPE_MAX_CHARS} serialized, a cut JSON string. */
    envelope: z.unknown(),
    violations: z.array(GateViolationSchema).min(1).max(20)
  })
  .refine(entry => rejectedEnvelopeSize(entry.envelope) <= REJECTED_ENVELOPE_MAX_CHARS, {
    message: `the envelope must serialize to at most ${REJECTED_ENVELOPE_MAX_CHARS} characters`,
    path: ['envelope']
  })
export type RejectedEnvelope = z.infer<typeof RejectedEnvelopeSchema>

/** Serialized length — what the entry costs in the report file, escapes included. */
function rejectedEnvelopeSize(envelope: unknown): number {
  return (JSON.stringify(envelope ?? null) ?? '').length
}

/**
 * An envelope as a rejected entry stores it: itself when it serializes within
 * the bound, else its JSON cut to the bound (a string, so it cannot be mistaken
 * for a parseable envelope). `undefined` — no structured output — is `null`.
 */
export function boundRejectedEnvelope(envelope: unknown): unknown {
  if (rejectedEnvelopeSize(envelope) <= REJECTED_ENVELOPE_MAX_CHARS) return envelope ?? null
  const text = typeof envelope === 'string' ? envelope : (JSON.stringify(envelope) ?? String(envelope))
  // Quotes and backslashes grow when the cut string is serialized again; shrink until it fits.
  let keep = REJECTED_ENVELOPE_MAX_CHARS - 3
  let cut = `${text.slice(0, keep)}…`
  while (rejectedEnvelopeSize(cut) > REJECTED_ENVELOPE_MAX_CHARS) {
    keep = Math.max(0, keep - (rejectedEnvelopeSize(cut) - REJECTED_ENVELOPE_MAX_CHARS))
    cut = `${text.slice(0, keep)}…`
  }
  return cut
}

/**
 * `~/phase-report.json`, rewritten atomically after every step and read by
 * `vm-run.ts` once, after the runner exits. `outcome` is `running` on every
 * rewrite but the last.
 *
 * NOT THE DURABLE RECORD. An agent phase runs as the runner's own user and
 * could rewrite this file; the ledger's phase events are what a resume trusts.
 */
export const PhaseReportSchema = z
  .object({
    runId: z.string().min(1),
    lane: AgentRunLaneSchema,
    laneVersion: z.string().min(1).max(64),
    runnerInvocationId: RunnerInvocationIdSchema,
    outcome: z.enum(['running', 'complete', 'parked', 'failed', 'stopped', 'range-end']),
    /** Index of the next step to execute; the lane's length once complete. */
    phaseCursor: z.number().int().min(0),
    parkQuestion: z.string().nullable(),
    lastViolation: GateViolationSchema.nullable(),
    acceptedEnvelopes: z.array(AcceptedEnvelopeSchema),
    /** The most recent rejected attempts, oldest first. Optional: reports written before it parse. */
    rejectedEnvelopes: z.array(RejectedEnvelopeSchema).max(REJECTED_ENVELOPES_MAX).optional(),
    /** The run's cumulative spend across every phase so far (KTD23). */
    costUsd: z.number().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    updatedAt: z.string().datetime()
  })
  // Exit 10 and exit 1 each owe `vm-run.ts` a reason it can post.
  .refine(report => report.outcome !== 'parked' || (report.parkQuestion !== null && report.lastViolation !== null), {
    message: 'a parked report carries its park question and the violation behind it',
    path: ['parkQuestion']
  })
  .refine(report => report.outcome !== 'failed' || report.lastViolation !== null, {
    message: 'a failed report carries the violation behind it',
    path: ['lastViolation']
  })
export type PhaseReport = z.infer<typeof PhaseReportSchema>
